import React from "react";
import * as yup from "yup";

import {useGlobalState} from "../../GlobalState";
import AttributeList from "../../components/AttributeList";
import TextAttributeForm from "../../components/AttributeForm/TextAttributeForm";
import RadioGroupForm from "../../components/AttributeForm/RadioGroupForm";
import useDialog from "../../hooks/useDialog";
import QuoteService from "../../services/quotes";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import TaskIcon from '@mui/icons-material/Task';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EuroIcon from '@mui/icons-material/Euro';
import Box from "@mui/material/Box";
import {useFeedbackTools} from "../../components/FeedbackTools";


const QuoteSettingsCard = () => {
    const {settings, updateSettings} = useGlobalState();
    const {showErrorNotification, showSuccessNotification} = useFeedbackTools();

    const formConfirmationPolicy = useDialog();
    const formCancellationPolicy = useDialog();
    const formDeposit = useDialog();
    const formQuoteExpirationInDays = useDialog();
    const formForewarningForRefundInDays = useDialog();
    const formTouristTaxPolicy = useDialog();
    const formDefaultTouristTaxUnitPrice = useDialog();

    const handleUpdateSetting = async (formDialog, data) => {
        try {
            await updateSettings(data);
            formDialog.close();
            showSuccessNotification("Salvataggio completato");
        } catch (e) {
            showErrorNotification(e);
        }
    }

    const attributes = [
        {
            label: "Politica conferma di default",
            value: QuoteService.utils.getConfirmationPolicyText(settings.defaultConfirmationPolicy),
            icon: <TaskIcon/>,
            formDialog: formConfirmationPolicy,
            formComponent: <RadioGroupForm attribute="defaultConfirmationPolicy"
                                           label="Politica conferma di default"
                                           options={Object.values(QuoteService.enums.CONFIRMATION_POLICY)
                                               .filter(e => e !== QuoteService.enums.CONFIRMATION_POLICY.creditCardGuarantee)
                                               .map(e => (
                                                   {label: QuoteService.utils.getConfirmationPolicyText(e), value: e})
                                               )
                                           }
                                           value={settings.defaultConfirmationPolicy}
                                           onClose={formConfirmationPolicy.close}
                                           onSubmit={(data) => handleUpdateSetting(formConfirmationPolicy, data)}
                                           isLoading={formConfirmationPolicy.loading}/>
        },
        {
            label: "Politica cancellazione di default",
            value: QuoteService.utils.getCancellationPolicyText(settings.defaultCancellationPolicy),
            icon: <EventBusyIcon/>,
            formDialog: formCancellationPolicy,
            formComponent: <RadioGroupForm attribute="defaultCancellationPolicy"
                                           label="Politica cancellazione di default"
                                           options={
                                               Object.values(QuoteService.enums.CANCELLATION_POLICY).map(e => (
                                                   {label: QuoteService.utils.getCancellationPolicyText(e), value: e})
                                               )
                                           }
                                           value={settings.defaultCancellationPolicy}
                                           onClose={formCancellationPolicy.close}
                                           onSubmit={(data) => handleUpdateSetting(formCancellationPolicy, data)}
                                           isLoading={formCancellationPolicy.loading}/>
        },
        {
            label: "Percentuale Caparra",
            value: settings.defaultDepositPercentage,
            icon: <Box textAlign="center" sx={{width: "24px"}}>%</Box>,
            defaultValue: 15,
            formDialog: formDeposit,
            formComponent: <TextAttributeForm attribute="defaultDepositPercentage"
                                              label="Percentuale caparra"
                                              validationSchema={yup.object().shape({
                                                  defaultDepositPercentage: yup.number()
                                                      .integer('Deve essere un numero intero')
                                                      .min(0, 'Deve essere maggiore o uguale a 0')
                                                      .max(100, 'Deve essere minore o uguale a 100')
                                                      .required('Campo obbligatorio')
                                                      .typeError('Deve essere un numero')
                                              })}
                                              value={settings.defaultDepositPercentage}
                                              onClose={formDeposit.close}
                                              onSubmit={(data) => handleUpdateSetting(formDeposit, data)}
                                              isLoading={formDeposit.loading}/>
        },
        {
            label: "Validità in giorni dei preventivi",
            value: settings.defaultQuoteExpirationInDays,
            icon: <HourglassTopIcon/>,
            defaultValue: 5,
            formDialog: formQuoteExpirationInDays,
            formComponent: <TextAttributeForm attribute="defaultQuoteExpirationInDays"
                                              label="Giorni di validità dei preventivi "
                                              validationSchema={yup.object().shape({
                                                  defaultQuoteExpirationInDays: yup.number()
                                                      .integer('Deve essere un numero intero')
                                                      .min(0, 'Deve essere maggiore o uguale a 0')
                                                      .required('Campo obbligatorio')
                                                      .typeError('Deve essere un numero')
                                              })}
                                              value={settings.defaultQuoteExpirationInDays}
                                              onClose={formQuoteExpirationInDays.close}
                                              onSubmit={(data) => handleUpdateSetting(formQuoteExpirationInDays, data)}
                                              isLoading={formQuoteExpirationInDays.loading}/>
        },
        {
            label: "Preavviso in giorni per la cancellazione con rimborso",
            value: settings.defaultForewarningForRefundInDays,
            icon: <AccessTimeIcon/>,
            defaultValue: 30,
            formDialog: formForewarningForRefundInDays,
            formComponent: <TextAttributeForm attribute="defaultForewarningForRefundInDays"
                                              label="Preavviso cancellazione in giorni"
                                              validationSchema={yup.object().shape({
                                                  defaultForewarningForRefundInDays: yup.number()
                                                      .integer('Deve essere un numero intero')
                                                      .min(0, 'Deve essere maggiore o uguale a 0')
                                                      .required('Campo obbligatorio')
                                                      .typeError('Deve essere un numero')
                                              })}
                                              value={settings.defaultForewarningForRefundInDays}
                                              onClose={formForewarningForRefundInDays.close}
                                              onSubmit={(data) => handleUpdateSetting(formForewarningForRefundInDays, data)}
                                              isLoading={formForewarningForRefundInDays.loading}/>
        },
        {
            label: "Politica Tassa di Soggiorno",
            value: QuoteService.utils.getTouristTaxPolicyText(settings.defaultTouristTaxPolicy),
            icon: <EuroIcon/>,
            formDialog: formTouristTaxPolicy,
            formComponent: <RadioGroupForm attribute="defaultTouristTaxPolicy"
                                           label="Politica tassa di soggiorno di default"
                                           options={Object.values(QuoteService.enums.TOURIST_TAX_POLICY)
                                               .map(e => (
                                                   {label: QuoteService.utils.getTouristTaxPolicyText(e), value: e})
                                               )
                                           }
                                           value={settings.defaultTouristTaxPolicy}
                                           onClose={formTouristTaxPolicy.close}
                                           onSubmit={(data) => handleUpdateSetting(formTouristTaxPolicy, data)}
                                           isLoading={formTouristTaxPolicy.loading}/>
        },
        {
            label: "Importo tassa di soggiorno a notte per persona",
            value: settings.defaultTouristTaxUnitPrice.toFixed(2),
            icon: <EuroIcon/>,
            defaultValue: 1.5,
            formDialog: formDefaultTouristTaxUnitPrice,
            formComponent: <TextAttributeForm attribute="defaultTouristTaxUnitPrice"
                                              label="Importo tassa di soggiorno a notte per persona"
                                              validationSchema={yup.object().shape({
                                                  defaultTouristTaxUnitPrice: yup.number()
                                                      .min(0, 'Deve essere maggiore o uguale a 0')
                                                      .required('Campo obbligatorio')
                                                      .typeError('Deve essere un numero')
                                              })}
                                              value={settings.defaultTouristTaxUnitPrice}
                                              onClose={formDefaultTouristTaxUnitPrice.close}
                                              onSubmit={(data) => handleUpdateSetting(formDefaultTouristTaxUnitPrice, data)}
                                              isLoading={formDefaultTouristTaxUnitPrice.loading}/>
        },
    ]

    return <Card sx={{position: "relative"}}>
        <CardContent>
            <Container maxWidth='sm' disableGutters>
                <AttributeList attributes={attributes}/>
            </Container>
        </CardContent>
    </Card>
}

export default QuoteSettingsCard;